/**
 * Chat Message Attachment Types
 *
 * Stored on message nodes in Neo4j as JSON.
 * S3 keys are never exposed to the client — all URLs go through our proxy.
 */

import type { PlanTier } from '../plans/types';

/**
 * How an attachment came to exist. `recording` marks audio captured by the
 * in-app recorder (mic button / meeting capture), set by the composer at stage
 * time and persisted through presign -> bind. A picked/dropped audio FILE never
 * carries it - that distinction drives rendering (recordings get a dedicated
 * full-width row in the message; uploads sit in the attachment grid). Absent on
 * every attachment created before the field existed.
 */
export type AttachmentOrigin = 'recording';

/**
 * Why a transcription ended in `error`. `empty` = the provider ran and found no
 * speech (silence, or a clip too short to carry any); `provider_failed` = the
 * provider call itself failed or returned no transcript object.
 */
export type TranscriptionErrorReason = 'empty' | 'provider_failed';

/**
 * Who a transcript label is. The transcript text carries speakers as plain
 * labels (`[mm:ss] Label: text`); this map is written next to it at transcript
 * time so no reader has to guess a person from a display name. It holds NO
 * image: a reader resolves the avatar from `userId` through its own surface
 * (member avatar proxy, or the guest's token-scoped URL).
 */
export interface TranscriptSpeaker {
	/** Exactly the label used in the transcript text. */
	label: string;
	/** Absent when the speaker was not matched to a person (the label is Scribe's "Speaker N"). */
	userId?: string;
	name: string;
	isGuest: boolean;
}

/**
 * The stored map as a reader may pass it on: the four known fields and nothing
 * else, so a value that ever rides along in storage (an image reference, an
 * address) cannot reach a client through this map.
 */
export function readTranscriptSpeakers(value: unknown): TranscriptSpeaker[] | undefined {
	if (!Array.isArray(value)) return undefined;
	const speakers: TranscriptSpeaker[] = [];
	for (const entry of value) {
		if (!entry || typeof entry !== 'object') continue;
		const { label, userId, name, isGuest } = entry as Record<string, unknown>;
		if (typeof label !== 'string' || !label || typeof name !== 'string') continue;
		speakers.push({ label, ...(typeof userId === 'string' && userId ? { userId } : {}), name, isGuest: isGuest === true });
	}
	return speakers.length ? speakers : undefined;
}

/** Stored in Neo4j on message nodes (server-side) */
export interface MessageAttachment {
	id: string;
	fileName: string;
	fileType: string; // MIME type
	fileSize: number; // bytes
	s3Key: string;
	origin?: AttachmentOrigin;
	width?: number; // images/videos only
	height?: number; // images/videos only
	duration?: number; // audio/video duration in seconds
	waveformData?: number[]; // normalized amplitude peaks (0-1) for audio visualization
	transcription?: string; // auto-transcribed text for audio attachments
	transcriptionSummary?: string; // one-shot gist written at transcribe time
	/** Present only on the post-call recording; uses the ordinary audio pipeline. */
	huddleId?: string;
	/** Huddle recordings only: one entry per label in `transcription`. Absent on recordings transcribed before it existed. */
	transcriptSpeakers?: TranscriptSpeaker[];
	/**
	 * Async transcription state. `pending` = queued, `processing` = in flight
	 * (the distinction only becomes visible on long audio, where the gap between
	 * the two is minutes rather than milliseconds).
	 */
	transcriptionStatus?: 'pending' | 'processing' | 'complete' | 'error';
	/**
	 * Only meaningful while `transcriptionStatus === 'error'` — readers gate on
	 * the status, never on this field alone, so a stale value can never be
	 * mistaken for a live one. `null` = explicitly cleared: the atomic
	 * attachment patch merges keys, so an explicit null is what blanks the
	 * field (an `undefined` would be stripped and leave the old reason behind).
	 */
	transcriptionError?: TranscriptionErrorReason | null;
}

/** Returned to the client (s3Key stripped, proxy URL added) */
export interface MessageAttachmentDisplay {
	id: string;
	fileName: string;
	fileType: string;
	fileSize: number;
	url: string; // /api/attachments/download/{id}
	huddleId?: string;
	origin?: AttachmentOrigin;
	// Same endpoint with ?thumbnail= — `preview` for images (sized for the chat
	// gallery), `true` for video/PDF/text posters (of which there is only one).
	thumbnailUrl?: string;
	width?: number;
	height?: number;
	duration?: number; // audio/video duration in seconds
	waveformData?: number[]; // normalized amplitude peaks for audio visualization
	transcription?: string; // auto-transcribed text for audio attachments
	transcriptionSummary?: string; // one-shot gist written at transcribe time
	/** See `MessageAttachment.transcriptSpeakers`. */
	transcriptSpeakers?: TranscriptSpeaker[];
	/** Async transcription state — see `MessageAttachment.transcriptionStatus`. */
	transcriptionStatus?: 'pending' | 'processing' | 'complete' | 'error';
	/**
	 * Why the transcription failed — see `MessageAttachment.transcriptionError`.
	 * Only meaningful while `transcriptionStatus === 'error'`; `null` = cleared.
	 */
	transcriptionError?: TranscriptionErrorReason | null;
}

/** Presign request body */
export interface PresignUploadRequest {
	graphId: string;
	threadId?: string;
	noteId?: string;
	fileName: string;
	fileType: string;
	fileSize: number;
	/**
	 * Audio only: client-measured duration in seconds. Audio is capped by
	 * duration (`AUDIO_RECORDING_MAX_SECONDS`) rather than by the agent-read
	 * budget, so the server needs this to enforce the ceiling. Upload paths
	 * already measure it for the waveform/player, so it costs nothing to send.
	 */
	audioDurationSec?: number;
	/**
	 * Set to `recording` by the in-app recorder's staging path only. Persisted
	 * onto the bound attachment so renderers can tell a captured recording from
	 * an uploaded audio file without sniffing filenames or MIME.
	 */
	origin?: AttachmentOrigin;
}

/** Presign response */
export interface PresignUploadResponse {
	attachmentId: string;
	uploadUrl: string;
	/**
	 * For video uploads only: a second presigned PUT URL for the client-captured
	 * poster frame (JPEG). Uploaded to a derived key alongside the video so the
	 * same still shown in the composer pre-submit is served as the embedded
	 * video's poster. Absent for non-video types.
	 */
	thumbnailUploadUrl?: string;
}

/**
 * Umbrella upload cap for the FREE tier (covers media/non-AI files like video).
 * Kept as the default/fallback so a caller that can't resolve a plan fails
 * closed to the smallest cap. Paid tiers get larger caps via
 * `MAX_ATTACHMENT_SIZE_BY_TIER` — see `getMaxSizeForMime`.
 */
export const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;

/**
 * Per-tier umbrella cap for files the agent does not read into context -
 * video, archives, and audio. Phone-recorded video routinely exceeds the free
 * 50MB cap (≈23s of 1080p H.264), which is why "cannot upload video from
 * mobile" surfaced - paid tiers lift the ceiling. Audio takes this cap too
 * (see `getMaxSizeForMime`): threads accept a recording of any length, and a
 * 10MB ceiling is roughly 11-44 minutes of opus. Agent-readable types the
 * agent DOES open raw (image/text/PDF/office docs) stay gated to
 * `MAX_AGENT_FILE_SIZE` on every tier.
 */
export const MAX_ATTACHMENT_SIZE_BY_TIER: Record<PlanTier, number> = {
	free: MAX_ATTACHMENT_SIZE, // 50 MB
	team: 500 * 1024 * 1024, // 500 MB
	max: 1024 * 1024 * 1024, // 1 GB
};

/**
 * Cap for files the agent pulls into context whole (image, text, PDF, office
 * docs). Gated tighter than the umbrella cap so the read tool can always open
 * them. Audio is deliberately NOT covered: it is pre-transcribed and only the
 * transcript reaches the agent - see `getMaxSizeForMime`.
 */
export const MAX_AGENT_FILE_SIZE = 10 * 1024 * 1024;

/** Max attachments per message */
export const MAX_ATTACHMENTS_PER_MESSAGE = 25;

export function isImageType(mimeType: string): boolean {
	return mimeType.startsWith('image/');
}

/**
 * HEIC/HEIF (iPhone camera default). Classified as an image everywhere, but
 * only Safari can decode it natively, so the backend serves a browser-renderable
 * webp transcode on plain download (raw bytes via `?original=true`) and the
 * client transcodes staged files for the pre-upload preview.
 */
export function isHeicType(mimeType: string): boolean {
	const m = mimeType.toLowerCase();
	return m === 'image/heic' || m === 'image/heif' || m === 'image/heic-sequence' || m === 'image/heif-sequence';
}

export function isVideoType(mimeType: string): boolean {
	return mimeType.startsWith('video/');
}

export function isPdfType(mimeType: string): boolean {
	return mimeType === 'application/pdf';
}

export function isAudioType(mimeType: string): boolean {
	return mimeType.startsWith('audio/');
}

/**
 * A recorder-captured audio attachment, by the persisted `origin` marker - the
 * ONE rule deciding the dedicated full-width row vs the attachment grid.
 * Deliberately NOT a filename/MIME sniff: an uploaded `recording.webm` is still
 * an upload. Attachments from before the marker existed return false and take
 * the legacy audio rendering (full-width only when they are the message's sole
 * attachment).
 */
export function isRecordingAttachment(att: { fileType: string; origin?: AttachmentOrigin }): boolean {
	return att.origin === 'recording' && isAudioType(att.fileType);
}

export function isTextType(mimeType: string): boolean {
	return mimeType.startsWith('text/');
}

/**
 * Document types we render a lazy "document page" poster JPEG for, server-side —
 * markdown, plain text, and RTF. Deliberately NARROWER than `isTextType`: we
 * exclude csv/tsv/html/json/xml and source-code files, none of which read as a
 * printed page at thumbnail size.
 *
 * Gated on the MIME strings these files ACTUALLY arrive as. The web upload path
 * runs the browser's `File.type` through `resolveFileMimeType`, which passes a
 * specific (non-empty, non-octet-stream) type through untouched — so the stored
 * `fileType` is whatever the browser reported:
 *   - `.txt` → `text/plain` (universal)
 *   - `.md`  → `text/markdown` (Chrome/Firefox); `text/x-markdown` is the older alias
 *   - `.rtf` → `text/rtf` (Chrome/Safari) or `application/rtf` (Firefox)
 * Bot/MCP uploads set their own `fileType`; these same strings cover them. (A `.md`
 * a browser reports as `''` collapses to `application/octet-stream` and is NOT
 * gated here — it renders no poster, same as any untyped upload.)
 */
export function isTextPosterType(mimeType: string): boolean {
	return (
		mimeType === 'text/plain' ||
		mimeType === 'text/markdown' ||
		mimeType === 'text/x-markdown' ||
		mimeType === 'application/rtf' ||
		mimeType === 'text/rtf'
	);
}

/** True when the text-poster renderer should apply markdown styling (vs uniform body text). */
export function isMarkdownPosterType(mimeType: string): boolean {
	return mimeType === 'text/markdown' || mimeType === 'text/x-markdown';
}

/**
 * Structured-text formats the agent reads via the plain-text path (UTF-8,
 * line-sliced). `text/xml` and `text/yaml` are already covered by `text/*`.
 */
export function isStructuredTextType(mimeType: string): boolean {
	return (
		mimeType === 'application/json' ||
		mimeType.endsWith('+json') || // application/ld+json, etc.
		mimeType === 'application/xml' ||
		mimeType.endsWith('+xml') || // application/atom+xml, etc.
		mimeType === 'application/yaml' ||
		mimeType === 'application/x-yaml'
	);
}

/**
 * Office / rich-document MIME types the agent reads via a format-specific
 * extractor (docx, spreadsheets, pptx, odt/odp/ods, legacy .doc, rtf, .eml).
 * Kept here so the readability gate and the backend extractor dispatch agree on
 * one list — a type readable here needs an extractor in `office-extractors.ts`.
 */
const OFFICE_DOC_MIME_TYPES = new Set<string>([
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
	'application/vnd.ms-excel', // xls
	'application/vnd.oasis.opendocument.spreadsheet', // ods
	'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
	'application/vnd.oasis.opendocument.text', // odt
	'application/vnd.oasis.opendocument.presentation', // odp
	'application/msword', // legacy .doc
	'application/rtf', // rtf
	'text/rtf', // rtf
	'message/rfc822', // .eml
]);

export function isOfficeDocType(mimeType: string): boolean {
	return OFFICE_DOC_MIME_TYPES.has(mimeType);
}

/**
 * Filename extensions that are plain text even when the browser mis-reports the
 * MIME (e.g. `.ts` source arrives as `video/mp2t`, unknown files as
 * `application/octet-stream`). Used as a fallback for the agent-read gate so
 * code/config/text uploads stay readable regardless of the reported type.
 */
const TEXT_FILE_EXTENSIONS = new Set<string>([
	// code
	'ts', 'tsx', 'mts', 'cts', 'js', 'jsx', 'mjs', 'cjs', 'py', 'rb', 'php',
	'go', 'rs', 'java', 'kt', 'kts', 'scala', 'swift', 'c', 'h', 'cpp', 'cc',
	'cxx', 'hpp', 'hh', 'cs', 'm', 'mm', 'lua', 'pl', 'pm', 'dart', 'ex', 'exs',
	'erl', 'hs', 'clj', 'cljs', 'groovy', 'gradle', 'r', 'vue', 'svelte',
	'astro', 'graphql', 'gql', 'proto',
	// shell / build
	'sh', 'bash', 'zsh', 'fish', 'ps1', 'bat', 'cmd', 'make', 'makefile',
	'cmake', 'dockerfile', 'gemfile', 'rakefile', 'procfile',
	// config / data
	'sql', 'toml', 'ini', 'cfg', 'conf', 'env', 'properties', 'lock', 'log',
	'csv', 'tsv', 'yaml', 'yml', 'xml', 'json', 'jsonc', 'gitignore',
	'gitattributes', 'dockerignore', 'editorconfig', 'diff', 'patch',
	// text / docs
	'md', 'markdown', 'mdx', 'rst', 'txt', 'text', 'tex', 'srt', 'vtt',
]);

export function isTextExtension(fileName: string): boolean {
	const ext = fileName.split('.').pop()?.toLowerCase();
	return !!ext && TEXT_FILE_EXTENSIONS.has(ext);
}

export function isPreviewableType(mimeType: string): boolean {
	return (
		isImageType(mimeType) ||
		isVideoType(mimeType) ||
		isPdfType(mimeType) ||
		isTextType(mimeType)
	);
}

/**
 * Extension → MIME fallback for files the browser hands us without a type.
 * Notably `.mov` screen recordings pasted/dragged into the composer often
 * arrive as `''` or `application/octet-stream`, which made them render as a
 * generic "file" (no preview, no poster, wrong size cap). Keep this focused on
 * the media types we preview.
 */
const EXTENSION_MIME: Record<string, string> = {
	// video
	mov: 'video/quicktime',
	mp4: 'video/mp4',
	m4v: 'video/x-m4v',
	webm: 'video/webm',
	mkv: 'video/x-matroska',
	avi: 'video/x-msvideo',
	ogv: 'video/ogg',
	// image
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	gif: 'image/gif',
	webp: 'image/webp',
	avif: 'image/avif',
	heic: 'image/heic',
	heif: 'image/heif',
};

/**
 * Resolve a file's MIME type, falling back to its extension when the browser
 * gives us nothing usable. Returns a specific provided type as-is; only fills
 * in when `providedType` is empty or the generic `application/octet-stream`.
 */
export function resolveFileMimeType(fileName: string, providedType?: string): string {
	if (providedType && providedType !== 'application/octet-stream') return providedType;
	const ext = fileName.split('.').pop()?.toLowerCase();
	if (ext && EXTENSION_MIME[ext]) return EXTENSION_MIME[ext];
	return providedType || 'application/octet-stream';
}

/**
 * MIME types the graph agent can open via the `read_attachment` tool.
 *
 * - `image/*`, `text/*`, `application/pdf`, structured text (JSON/XML/YAML)
 *   → opened directly.
 * - `audio/*` → pre-transcribed at upload; the transcription is inlined into
 *   the `[Attachments: ...]` annotation, so the agent reads audio without
 *   calling the tool.
 * - office / rich documents (docx, spreadsheets, pptx, odt/odp/ods, legacy
 *   .doc, rtf, .eml) → opened via a format-specific text extractor.
 * - when `fileName` is given, code/config/text files the browser mis-typed
 *   (e.g. `.ts` reported as `video/mp2t`) are readable via the extension.
 *
 * Anything outside this set (video, archives) is upload-only: users can share
 * it for human consumption, but the agent surfaces a "can't open this" stub and
 * the UI shows an AI-not-readable badge.
 */
export function isAgentReadable(mimeType: string, fileName?: string): boolean {
	return (
		isImageType(mimeType) ||
		mimeType.startsWith('text/') ||
		isStructuredTextType(mimeType) ||
		isPdfType(mimeType) ||
		isAudioType(mimeType) ||
		isOfficeDocType(mimeType) ||
		(!!fileName && isTextExtension(fileName))
	);
}

/**
 * Per-MIME upload cap. Agent-readable types must fit the agent's read budget
 * so they're guaranteed openable (tier-independent); everything else gets the
 * plan's umbrella cap. `tier` defaults to `'free'` so an unresolved plan fails
 * closed to the smallest cap.
 *
 * Audio is the one exception, and it is deliberate. The agent-read budget
 * exists so the read tool can pull a whole file into context, and that simply
 * does not apply to audio: audio is pre-transcribed at upload, only the
 * transcript ever reaches the agent (inlined into the `[Attachments: ...]`
 * annotation), and `read_attachment` on an audio file returns that
 * transcription rather than the bytes. The 10MB ceiling is a constraint about
 * text, PDFs and images. Holding audio to it would cap a recording at roughly
 * 11-44 minutes of opus, and threads are meant to accept audio of any length,
 * so audio takes the plan tier cap - the same one video already uses.
 *
 * `isAgentReadable` itself is left alone: audio IS agent-readable in the sense
 * that matters to the read tool and the AI-not-readable badge. Only the size
 * budget diverges.
 */
export function getMaxSizeForMime(
	mimeType: string,
	tier: PlanTier = 'free',
	fileName?: string,
): number {
	if (isAudioType(mimeType)) return MAX_ATTACHMENT_SIZE_BY_TIER[tier];
	return isAgentReadable(mimeType, fileName)
		? MAX_AGENT_FILE_SIZE
		: MAX_ATTACHMENT_SIZE_BY_TIER[tier];
}

/** Hard cap for audio recording (seconds) — fallback when plan lookup fails */
export const AUDIO_RECORDING_MAX_SECONDS = 28800; // 8 hours (matches PRO default)
